import { useEffect, useRef, useState } from "react";
import { SidebarComponent, TreeViewComponent, type NodeClickEventArgs, type NodeExpandEventArgs } from "@syncfusion/ej2-react-navigations";
import { TextBoxComponent } from "@syncfusion/ej2-react-inputs";
import { ButtonComponent } from "@syncfusion/ej2-react-buttons";
import { useStore } from "../../store";
import { useNavigate, useLocation } from "react-router-dom";
import type { TreeNode } from "../routes/treeBuilder";
import { useTranslation } from "react-i18next";

function Sidebar({sidebarOpen} : {sidebarOpen : boolean}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [filteredTree, setFilteredTree] = useState<TreeNode[]>([]);
  const [searchText, setSearchText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const { treeData, setSelectedNodeId } = useStore();
  const treeRef = useRef<TreeViewComponent>(null);  

  const searchInputRef = useRef<TextBoxComponent>(null);
  //local tree is only for translating - the items itself do not change
  const [localTreeData, setLocalTreeData] = useState<TreeNode[]>(treeData);
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);



  const handleTreeClick = (args: NodeClickEventArgs) => {
    const node = args.node;
    if (node) {
      const nodeId = node.getAttribute('data-uid');
      const clickedNode = localTreeData.find(item => item.id === nodeId);
      if (clickedNode?.url && nodeId && !clickedNode.hasChild) {
        const url = clickedNode.url.startsWith('/') ? clickedNode.url : `/${clickedNode.url}`;
        navigate(url);
        setSelectedNodes([nodeId]);
        setSelectedNodeId(nodeId);
        resetSearch();
      }
    }
  };

  const handleNodeSelecting = (args: any) => {
    const nodeId = args.nodeData?.id;
    if (nodeId) {
      const node = localTreeData.find(item => item.id === nodeId);
      if (node?.hasChild) {
        args.cancel = true;
      }
    }
  };

  // Filter tree nodes based on search text
  const filterTreeNodes = (nodes: TreeNode[], searchTerm: string): TreeNode[] => {
    if (!searchTerm.trim()) return nodes;
    
    const filtered: TreeNode[] = [];
    const searchLower = searchTerm.toLowerCase();
    
    const filterNode = (node: TreeNode): boolean => {
      const matchesSearch = node.name.toLowerCase().includes(searchLower);
      
      // Check if any children match
      const children = nodes.filter(n => n.pid === node.id);
      const matchingChildren = children.filter(child => filterNode(child));
      
      if (matchesSearch || matchingChildren.length > 0) {
        // Add this node and its matching children
        filtered.push(node);
        matchingChildren.forEach(child => {
          if (!filtered.find(n => n.id === child.id)) {
            filtered.push(child);
          }
        });
        return true;
      }
      
      return false;
    };
    
    // Start with root nodes (nodes without parent)
    const rootNodes = nodes.filter(node => !node.pid);
    rootNodes.forEach(node => filterNode(node));
    
    return filtered;
  };

  // Handle search text change
  const handleSearchChange = (e: any) => {
    const value = e.value || '';
    setSearchText(value);
    setIsSearching(value.trim().length > 0);
    
    if (value.trim()) {
      const filtered = filterTreeNodes(localTreeData, value);
      setFilteredTree(filtered);
      
      // Auto-navigate if only one node remains
      if (filtered.length === 1) {
        const singleNode = filtered[0];
        if (singleNode.url) {
          const url = singleNode.url.startsWith('/') ? singleNode.url : `/${singleNode.url}`;
          navigate(url);
          resetSearch();
        }
      }
    } else {
      setFilteredTree([]);
    }
  };

  // Handle Enter key press
  const handleSearchKeyPress = (e: any) => {
    if (e.key === 'Enter') {
      if (filteredTree.length === 1) {
        // Navigate to the single result
        const singleNode = filteredTree[0];
        if (singleNode.url) {
          const url = singleNode.url.startsWith('/') ? singleNode.url : `/${singleNode.url}`;
          navigate(url);
          resetSearch();
        }
      } else if (filteredTree.length > 1) {
        // Expand all filtered nodes to show them
        if (treeRef.current) {
          treeRef.current.expandAll();
        }
      }
    }
  };

  // Reset search functionality
  const resetSearch = () => {
    setSearchText('');
    setIsSearching(false);
    setFilteredTree([]);
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  // Update tree nodes when language changes
  useEffect(() => {
    const updatedNodes = treeData.map(node => ({
      ...node,
      name: t(node.labelKey)
    }));
    setLocalTreeData(updatedNodes);
  }, [i18n.language, treeData, t]);

  // Update tree view when local data changes
  useEffect(() => {
    if (treeRef.current) {
      const dataToShow = isSearching ? filteredTree : localTreeData;
      treeRef.current.fields = {
        ...treeRef.current.fields,
        dataSource: dataToShow
      };
      // Expand all nodes after a short delay to ensure the data is loaded
      setTimeout(() => {
        if (treeRef.current) {
          treeRef.current.expandAll();
        }
      }, 0);
    }
  }, [localTreeData, filteredTree, isSearching]);

  // Highlight current menu item based on URL
  useEffect(() => {
    if (localTreeData.length > 0) {
      const currentPath = location.pathname;
      
      // Find the node that matches the current path
      const findNodeByPath = (nodes: TreeNode[], path: string): TreeNode | null => {
        for (const node of nodes) {
          if (node.url && path.includes(node.url)) {
            return node;
          }
          // Check children recursively
          const children = localTreeData.filter(n => n.pid === node.id);
          const childMatch = findNodeByPath(children, path);
          if (childMatch) return childMatch;
        }
        return null;
      };
      
      const matchingNode = findNodeByPath(localTreeData, currentPath);
      if (matchingNode) {
        setSelectedNodes([matchingNode.id]);
        setSelectedNodeId(matchingNode.id);
      } else {
        // Clear selection if no matching node
        setSelectedNodes([]);
        setSelectedNodeId(null);
      }
    }
  }, [location.pathname, localTreeData, setSelectedNodeId]);

  return (
    <SidebarComponent      
      isOpen={sidebarOpen}
      type="Over"
      style={{ background: "#eeeeee", position: "relative", borderRight: '1px solid #aaaaaa' }}
      enableDock={true}
      dockSize="0px"
      width="350px"
      position="Left"
      animate={false}
    >    
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-1 flex-1">
          {sidebarOpen && (
            <>
              <div onKeyDown={handleSearchKeyPress}>
                <TextBoxComponent
                  ref={searchInputRef}
                  placeholder={t('actions.search')}
                  floatLabelType="Never"
                  cssClass="e-outline"
                  value={searchText}
                  change={handleSearchChange}
                />
              </div>
              {isSearching && (
                <ButtonComponent 
                  iconCss="e-icons e-close" 
                  cssClass="e-flat" 
                  onClick={resetSearch}
                  title={t('actions.clear')}
                />
              )}
              {!isSearching && (
                <ButtonComponent 
                  iconCss="e-icons e-search" 
                  cssClass="e-flat" 
                />
              )}
            </>
          )}  
        </div>
      </div>
      <TreeViewComponent
        ref={treeRef}
        cssClass="app-sidebar-treeview"
        fullRowSelect={true}
        animation={{
          expand: { effect: "SlideDown", duration: 0 },
          collapse: { effect: "SlideUp", duration: 0 },
        }}
        fields={{
          dataSource: isSearching ? filteredTree : localTreeData,
          parentID: "pid",
          text: "name",
          hasChildren: "hasChild",
          iconCss: "iconCss",
          id: "id"
        }}
        selectedNodes={selectedNodes}
        expandOn="None"
        nodeCollapsing={(args : NodeExpandEventArgs) => args.cancel=true}
        nodeSelecting={handleNodeSelecting}
        nodeClicked={handleTreeClick}
      />
    </SidebarComponent>
  );
}

export default Sidebar;
